import { useEffect } from 'react';
import { isFocusOnInput } from '@/features/production-settings/hotkeys/useHotkeyEngine';
import { useCurrencyDisplayStore } from '../store/currency-display-store';

/**
 * BLU-373 PR2b: Shift+C toggles the PO-page currency display between
 * the PO's currency and the org base currency.
 *
 * Why Shift+C and not just C: Radix Select supports type-ahead — typing
 * a letter while a Select trigger is focused jumps to the matching option.
 * The PO page has a status Select with a "cancelled" option; pressing
 * plain C with the Select focused (or merely after clicking it) would
 * jump to "cancelled" AND fire our toggle. Modifier-gated keys are immune
 * to type-ahead. "Shift+C" is still mnemonic for "change currency".
 *
 * Scoped — only mounts a listener while the calling component is alive.
 * Reuses the production-settings `isFocusOnInput` helper to avoid
 * hijacking the chord while the user is typing.
 *
 * Pass enabled=false to suppress (e.g. when toggle pill itself is hidden
 * because po.currency === org.default_currency).
 */
export function useCurrencyToggleHotkey(enabled: boolean = true) {
  const toggle = useCurrencyDisplayStore((s) => s.toggle);

  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent) => {
      // Shift+C only — plain C conflicts with Radix Select type-ahead
      // (e.g. status select jumping to "cancelled")
      if ((event.key !== 'c' && event.key !== 'C') || !event.shiftKey) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isFocusOnInput()) return;
      event.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, toggle]);
}
